import React from 'react'
import styles from './ChoosePlanModal.module.css'
import NewModal from '../NewModal/NewModal'
import { PLANS, priceOf } from './plans'

/**
 * Plan-selection sheet. Controlled: the selected plan + billing period live in
 * the parent so the page's fixed footer (TrustRow + CheckoutBar) reflects them.
 * This modal contains ONLY the selection UI — no trust row / checkout bar.
 */
const ChoosePlanModal = ({ isOpen, onClose, yearly, setYearly, selected, setSelected }) => {
  const [expanded, setExpanded] = React.useState(null)
  // Refs to each plan card so we can scroll the SELECTED (agent-recommended)
  // one into view when the sheet opens — otherwise a bottom plan like Growth
  // stays below the fold and the user doesn't see it's the recommendation.
  const cardRefs = React.useRef({})
  React.useEffect(() => {
    if (!isOpen || !selected) return undefined
    // Wait for the sheet's slide-up + layout to settle before scrolling.
    const t = setTimeout(() => {
      const el = cardRefs.current[selected]
      if (el && el.scrollIntoView) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' })
      }
    }, 400)
    return () => clearTimeout(t)
  }, [isOpen, selected])

  return (
    <NewModal
      isOpen={isOpen}
      onClose={onClose}
      title={null}
      height="auto"
      bodyMaxHeight="90vh"
      closeBtnPosition="top"
      bodyStyle={{ overflow: 'hidden' }}
    >
      <div className={styles.modal}>
        <h2 className={styles.title}>Choose your Plan</h2>

        {/* Billing toggle */}
        <div className={styles.toggleRow}>
          <span className={`${styles.toggleLabel} ${!yearly ? styles.toggleActive : ''}`}>Monthly</span>
          <button
            type="button"
            className={`${styles.switch} ${yearly ? styles.switchOn : ''}`}
            onClick={() => setYearly((v) => !v)}
            aria-label="Toggle yearly billing"
          >
            <span className={styles.knob} />
          </button>
          <span className={`${styles.toggleLabel} ${yearly ? styles.toggleActive : ''}`}>Yearly</span>
          <span className={styles.savePill}>Annual (Save up to 17%)</span>
        </div>

        {/* Plans */}
        <div className={styles.plans}>
          {PLANS.map((p) => {
            const isSel = selected === p.key
            const isOpenFeat = expanded === p.key
            return (
              <div
                key={p.key}
                ref={(el) => (cardRefs.current[p.key] = el)}
                className={`${styles.planCard} ${isSel ? styles.planCardSelected : ''} ${isSel ? styles[`accent_${p.key}`] : ''}`}
                onClick={() => setSelected(p.key)}
              >
                {p.recommended && (
                  <span className={styles.recommendedTag}>
                    <img src="/svg/recoomeded_shape.svg" alt="recommended" />
                  </span>
                )}

                <div className={styles.planTop}>
                  <div className={styles.planLeft}>
                    <img className={styles.planIcon} src={p.icon} alt={p.name} />
                    <div>
                      <h3 className={styles.planPrice}>${priceOf(p, yearly)}/month</h3>
                      <p className={styles.planPer}>Per Receptionist</p>
                    </div>
                  </div>
                  <div className={styles.planRight}>
                    <p className={styles.planMinutes}>
                      <strong>{p.minutes}</strong>
                      <span>minutes</span>
                    </p>
                    <div className={styles.radioWrapper}>
                      <span className={`${styles.radio} ${isSel ? styles.radioActive : ''}`} aria-hidden="true">
                        {isSel && <span className={styles.radioCheck}>✓</span>}
                      </span>
                    </div>
                  </div>
                </div>

                <div className={styles.planMid}>
                  <h4 className={styles.planName}>{p.name}</h4>
                  <button
                    type="button"
                    className={styles.featuresToggle}
                    onClick={(e) => {
                      e.stopPropagation()
                      setExpanded(isOpenFeat ? null : p.key)
                    }}
                  >
                    See All Features{' '}
                    <span className={`${styles.featuresArrow} ${isOpenFeat ? styles.featuresArrowOpen : ''}`}>⌄</span>
                  </button>
                </div>

                {!isOpenFeat ? (
                  <p className={styles.planSummary}>{p.short}</p>
                ) : (
                  <ul className={styles.fullFeatures}>
                    {p.features.map((f) => (
                      <li key={f}>{f}</li>
                    ))}
                  </ul>
                )}
              </div>
            )
          })}
        </div>
      </div>
    </NewModal>
  )
}

export default ChoosePlanModal
